import { z } from 'zod';
import {
  Channel,
  Confidence,
  CurrencyCode,
  DecimalString,
  Id,
  JsonObject,
  JsonValue,
  LanguageTag,
  RiskTier,
  Timestamp,
} from './common';

// ---------------------------------------------------------------------------
// People and identities
// ---------------------------------------------------------------------------

export const Person = z.object({
  person_id: Id,
  /** Display name shown in the UI; derived from first_name + last_name once onboarding is done. */
  display_name: z.string().max(200),
  first_name: z.string().max(100).optional(),
  last_name: z.string().max(100).optional(),
  email: z.email().optional(),
  /** Cohort admission: waitlisted people can sign in but the agent only sends the waitlist message. */
  admission: z.enum(['admitted', 'waitlisted']).default('admitted'),
  preferred_language: LanguageTag.default('ar'),
  timezone: z.string().default('Asia/Qatar'),
  locale: z.string().optional(),
  status: z.enum(['active', 'suspended', 'deleted']).default('active'),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type Person = z.infer<typeof Person>;

export const ChannelIdentity = z.object({
  channel_identity_id: Id,
  person_id: Id,
  channel: Channel,
  /** Channel-native user identifier: web session subject, Telegram user id, WhatsApp phone (E.164). */
  external_id: z.string().min(1),
  handle: z.string().optional(),
  verified: z.boolean().default(false),
  verified_at: Timestamp.optional(),
  metadata: JsonObject.optional(),
  created_at: Timestamp,
});
export type ChannelIdentity = z.infer<typeof ChannelIdentity>;

// ---------------------------------------------------------------------------
// Conversations and threads
// ---------------------------------------------------------------------------

export const Conversation = z.object({
  conversation_id: Id,
  person_id: Id,
  channel: Channel,
  channel_identity_id: Id,
  /** Channel-native chat id when the channel has one (Telegram chat_id, WhatsApp wa_id). */
  external_conversation_id: z.string().optional(),
  title: z.string().optional(),
  status: z.enum(['open', 'archived']).default('open'),
  last_event_at: Timestamp.optional(),
  created_at: Timestamp,
});
export type Conversation = z.infer<typeof Conversation>;

export const Thread = z.object({
  thread_id: Id,
  conversation_id: Id,
  person_id: Id,
  root_event_id: Id.optional(),
  topic: z.string().optional(),
  task_id: Id.optional(),
  status: z.enum(['open', 'resolved', 'archived']).default('open'),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type Thread = z.infer<typeof Thread>;

// ---------------------------------------------------------------------------
// Memory
// ---------------------------------------------------------------------------

export const MemoryType = z.enum([
  'fact',
  'preference',
  'relationship',
  'commitment',
  'routine',
  'credential_hint',
  'summary',
  'other',
]);
export type MemoryType = z.infer<typeof MemoryType>;

export const MemoryItem = z.object({
  memory_id: Id,
  person_id: Id,
  type: MemoryType,
  content: z.string().min(1),
  language: LanguageTag.optional(),
  source_event_id: Id.optional(),
  confidence: Confidence,
  /** Vectorize vector id when embedded. */
  vector_id: z.string().optional(),
  derived_from: z.array(Id).default([]),
  expires_at: Timestamp.optional(),
  deleted_at: Timestamp.optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type MemoryItem = z.infer<typeof MemoryItem>;

// ---------------------------------------------------------------------------
// Policy
// ---------------------------------------------------------------------------

export const ActionClass = z.enum([
  'read',
  'communicate',
  'schedule',
  'purchase',
  'transfer',
  'account_change',
  'data_export',
  'delete',
  'other',
]);
export type ActionClass = z.infer<typeof ActionClass>;

export const Recurrence = z.enum(['once', 'daily', 'weekly', 'monthly', 'unbounded']);
export type Recurrence = z.infer<typeof Recurrence>;

export const TimeWindow = z.object({
  /** RFC-3339 or "HH:mm" local-time boundaries interpreted in the person's timezone. */
  start: z.string().optional(),
  end: z.string().optional(),
  days_of_week: z.array(z.number().int().min(0).max(6)).optional(),
});
export type TimeWindow = z.infer<typeof TimeWindow>;

export const PolicyEffect = z.enum(['allow', 'require_approval', 'deny']);
export type PolicyEffect = z.infer<typeof PolicyEffect>;

export const Policy = z.object({
  policy_id: Id,
  person_id: Id,
  action_class: ActionClass,
  /** Tool name this policy applies to, or "*" for any tool. */
  tool: z.string().default('*'),
  /** Connected account (Connection) this policy applies to, or "*". */
  account: z.string().default('*'),
  /** Counterparty identifier (payee, recipient, merchant) or "*". */
  counterparty: z.string().default('*'),
  amount_cap: DecimalString.optional(),
  currency: CurrencyCode.optional(),
  time_window: TimeWindow.optional(),
  recurrence: Recurrence.default('unbounded'),
  risk_tier: RiskTier,
  effect: PolicyEffect,
  version: z.number().int().positive(),
  active: z.boolean().default(true),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type Policy = z.infer<typeof Policy>;

export const PolicyDecision = z.object({
  effect: PolicyEffect,
  policy_id: Id.optional(),
  policy_version: z.number().int().positive().optional(),
  reason: z.string(),
  evaluated_at: Timestamp,
});
export type PolicyDecision = z.infer<typeof PolicyDecision>;

// ---------------------------------------------------------------------------
// Connections (external accounts)
// ---------------------------------------------------------------------------

export const Connection = z.object({
  connection_id: Id,
  person_id: Id,
  provider: z.string().min(1),
  /** Provider account identifier (email, account number suffix, etc.). Never a secret. */
  account_label: z.string().min(1),
  scopes: z.array(z.string()).default([]),
  status: z.enum(['pending', 'active', 'expired', 'revoked', 'error']).default('pending'),
  /** Reference to the secret material, e.g. a KV key or Secrets Store id. Never the secret itself. */
  credential_ref: z.string().optional(),
  expires_at: Timestamp.optional(),
  last_used_at: Timestamp.optional(),
  metadata: JsonObject.optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type Connection = z.infer<typeof Connection>;

// ---------------------------------------------------------------------------
// Tasks, steps, actions
// ---------------------------------------------------------------------------

export const TaskState = z.enum([
  'understood',
  'planned',
  'awaiting_approval',
  'executing',
  'waiting',
  'verifying',
  'done',
  /** M5.1: a prepare-only outcome; nothing was created or sent. Terminal, never "done". */
  'prepared',
  'failed',
  'unknown',
  'compensating',
  'cancelled',
]);
export type TaskState = z.infer<typeof TaskState>;

/** Allowed transitions of the Task state machine. Terminal states have no outgoing edges. */
export const TASK_TRANSITIONS: Readonly<Record<TaskState, readonly TaskState[]>> = {
  understood: ['planned', 'cancelled', 'failed'],
  planned: ['awaiting_approval', 'executing', 'cancelled', 'failed'],
  awaiting_approval: ['executing', 'cancelled', 'failed'],
  executing: [
    'waiting',
    'verifying',
    'done',
    'prepared',
    'failed',
    'unknown',
    'compensating',
    'cancelled',
  ],
  waiting: ['executing', 'verifying', 'failed', 'unknown', 'cancelled'],
  verifying: ['done', 'failed', 'unknown', 'compensating'],
  unknown: ['verifying', 'compensating', 'failed', 'done'],
  compensating: ['failed', 'cancelled', 'done'],
  done: [],
  prepared: [],
  failed: [],
  cancelled: [],
};

export function canTransition(from: TaskState, to: TaskState): boolean {
  return TASK_TRANSITIONS[from].includes(to);
}

export const Task = z.object({
  task_id: Id,
  person_id: Id,
  conversation_id: Id.optional(),
  thread_id: Id.optional(),
  origin_event_id: Id.optional(),
  intent: z.string().min(1),
  summary: z.string().optional(),
  state: TaskState,
  risk_tier: RiskTier,
  plan: JsonValue.optional(),
  workflow_instance_id: z.string().optional(),
  error: z.string().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
  completed_at: Timestamp.optional(),
});
export type Task = z.infer<typeof Task>;

export const StepStatus = z.enum(['pending', 'running', 'done', 'failed', 'skipped']);
export type StepStatus = z.infer<typeof StepStatus>;

export const Step = z.object({
  step_id: Id,
  task_id: Id,
  index: z.number().int().nonnegative(),
  title: z.string().min(1),
  description: z.string().optional(),
  status: StepStatus.default('pending'),
  depends_on: z.array(Id).default([]),
  started_at: Timestamp.optional(),
  finished_at: Timestamp.optional(),
  created_at: Timestamp,
});
export type Step = z.infer<typeof Step>;

export const ActionStatus = z.enum([
  'proposed',
  'approved',
  'rejected',
  'executing',
  'succeeded',
  'failed',
  'unknown',
  'compensated',
]);
export type ActionStatus = z.infer<typeof ActionStatus>;

export const Action = z.object({
  action_id: Id,
  task_id: Id,
  step_id: Id.optional(),
  person_id: Id,
  tool: z.string().min(1),
  action_class: ActionClass,
  input: JsonObject.default({}),
  /** Stable key so retries never execute the same side effect twice. */
  idempotency_key: z.string().min(1),
  risk_tier: RiskTier,
  policy_decision: PolicyDecision,
  approval_id: Id.optional(),
  status: ActionStatus.default('proposed'),
  result: JsonValue.optional(),
  error: z.string().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type Action = z.infer<typeof Action>;

// ---------------------------------------------------------------------------
// Approvals, transactions, evidence
// ---------------------------------------------------------------------------

export const Approval = z.object({
  approval_id: Id,
  person_id: Id,
  task_id: Id,
  action_id: Id.optional(),
  channel: Channel,
  /** What the person is being asked to approve, rendered in their language. */
  prompt: z.string().min(1),
  status: z.enum(['pending', 'approved', 'rejected', 'expired']).default('pending'),
  decided_via_event_id: Id.optional(),
  requested_at: Timestamp,
  decided_at: Timestamp.optional(),
  expires_at: Timestamp.optional(),
});
export type Approval = z.infer<typeof Approval>;

export const Transaction = z.object({
  transaction_id: Id,
  person_id: Id,
  action_id: Id,
  connection_id: Id.optional(),
  amount: DecimalString,
  currency: CurrencyCode,
  counterparty: z.string().optional(),
  external_reference: z.string().optional(),
  status: z.enum(['pending', 'settled', 'failed', 'reversed', 'unknown']).default('pending'),
  occurred_at: Timestamp,
  created_at: Timestamp,
});
export type Transaction = z.infer<typeof Transaction>;

export const Evidence = z.object({
  evidence_id: Id,
  person_id: Id,
  task_id: Id.optional(),
  action_id: Id.optional(),
  kind: z.enum(['screenshot', 'receipt', 'api_response', 'email', 'log', 'other']),
  /** R2 key or inline content hash. */
  r2_key: z.string().optional(),
  content_hash: z.string().optional(),
  summary: z.string().optional(),
  created_at: Timestamp,
});
export type Evidence = z.infer<typeof Evidence>;

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const Notification = z.object({
  notification_id: Id,
  person_id: Id,
  channel: Channel,
  channel_identity_id: Id.optional(),
  task_id: Id.optional(),
  kind: z.enum(['info', 'approval_request', 'result', 'alert', 'digest']),
  body: z.string().min(1),
  language: LanguageTag.optional(),
  status: z.enum(['queued', 'sent', 'delivered', 'read', 'failed']).default('queued'),
  event_id: Id.optional(),
  created_at: Timestamp,
  sent_at: Timestamp.optional(),
});
export type Notification = z.infer<typeof Notification>;

// ---------------------------------------------------------------------------
// Observability: model calls, tool calls, human interventions, audit
// ---------------------------------------------------------------------------

export const ModelProvider = z.enum(['anthropic', 'openai', 'workers-ai', 'other']);
export type ModelProvider = z.infer<typeof ModelProvider>;

export const ModelRole = z.enum(['agent', 'classifier', 'extractor', 'embedder', 'judge']);
export type ModelRole = z.infer<typeof ModelRole>;

export const ModelCall = z.object({
  model_call_id: Id,
  person_id: Id.optional(),
  task_id: Id.optional(),
  event_id: Id.optional(),
  role: ModelRole,
  provider: ModelProvider,
  model: z.string().min(1),
  /** Whether this call was served by the fallback provider. */
  fallback: z.boolean().default(false),
  gateway_log_id: z.string().optional(),
  input_tokens: z.number().int().nonnegative().optional(),
  output_tokens: z.number().int().nonnegative().optional(),
  latency_ms: z.number().int().nonnegative().optional(),
  cost_usd: DecimalString.optional(),
  status: z.enum(['ok', 'error', 'timeout', 'rate_limited']),
  error: z.string().optional(),
  created_at: Timestamp,
});
export type ModelCall = z.infer<typeof ModelCall>;

export const ToolCall = z.object({
  tool_call_id: Id,
  person_id: Id.optional(),
  task_id: Id.optional(),
  action_id: Id.optional(),
  model_call_id: Id.optional(),
  tool: z.string().min(1),
  input: JsonObject.default({}),
  output: JsonValue.optional(),
  status: z.enum(['ok', 'error', 'denied', 'timeout']),
  error: z.string().optional(),
  latency_ms: z.number().int().nonnegative().optional(),
  created_at: Timestamp,
});
export type ToolCall = z.infer<typeof ToolCall>;

export const HumanIntervention = z.object({
  intervention_id: Id,
  person_id: Id,
  task_id: Id.optional(),
  actor: z.enum(['person', 'operator']),
  kind: z.enum(['approve', 'reject', 'correct', 'cancel', 'takeover', 'escalate', 'note']),
  note: z.string().optional(),
  event_id: Id.optional(),
  created_at: Timestamp,
});
export type HumanIntervention = z.infer<typeof HumanIntervention>;

/**
 * Audit events are append-only and record who did what to which entity.
 * Application code must never UPDATE or DELETE rows in `audit_events`.
 */
export const AuditEvent = z.object({
  audit_event_id: Id,
  person_id: Id.optional(),
  actor: z.enum(['person', 'agent', 'operator', 'system']),
  action: z.string().min(1),
  entity_type: z.string().min(1),
  entity_id: Id,
  before: JsonValue.optional(),
  after: JsonValue.optional(),
  metadata: JsonObject.optional(),
  created_at: Timestamp,
});
export type AuditEvent = z.infer<typeof AuditEvent>;

// ---------------------------------------------------------------------------
// Intents, cohorts, invites (M1)
// ---------------------------------------------------------------------------

export const IntentRecord = z.object({
  intent_id: Id,
  event_id: Id,
  person_id: Id,
  intent: z.enum(['chat', 'task', 'approval', 'correction']),
  confidence: Confidence,
  model: z.string().optional(),
  /** Raw classifier output, kept for debugging. */
  raw: z.string().optional(),
  trace_id: Id.optional(),
  created_at: Timestamp,
});
export type IntentRecord = z.infer<typeof IntentRecord>;

export const Cohort = z.object({
  cohort_id: Id,
  name: z.string().min(1),
  capacity: z.number().int().nonnegative(),
  active: z.boolean().default(true),
  created_at: Timestamp,
});
export type Cohort = z.infer<typeof Cohort>;

export const Invite = z.object({
  invite_id: Id,
  code: z.string().min(6),
  cohort_id: Id.optional(),
  created_by: Id,
  redeemed_by: Id.optional(),
  redeemed_at: Timestamp.optional(),
  created_at: Timestamp,
});
export type Invite = z.infer<typeof Invite>;
